import type { AdminGuideListItem, AdminGuideStatusGroup } from '@/features/admin/api/admin.api';
import type { StatusBadgeVariant } from '@/shared/components/ui/StatusBadge';
import { GuideStatus } from '@/types';

export const STATUS_GROUP_OPTIONS: ReadonlyArray<{ value: AdminGuideStatusGroup; label: string }> = [
  { value: 'approved', label: 'Approved' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'draft', label: 'Draft' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'suspended', label: 'Suspended' },
];

export const STATUS_GROUP_LABEL = Object.fromEntries(
  STATUS_GROUP_OPTIONS.map((option) => [option.value, option.label])
) as Record<AdminGuideStatusGroup, string>;

export function isStatusGroup(value: string | null): value is AdminGuideStatusGroup {
  return STATUS_GROUP_OPTIONS.some((option) => option.value === value);
}

/** Same grouping the server filters by, so a row's badge matches the filter that found it. */
export function statusGroupOf(status?: string): AdminGuideStatusGroup {
  switch (status) {
    case GuideStatus.APPROVED:
      return 'approved';
    case GuideStatus.SUBMITTED:
    case GuideStatus.UNDER_REVIEW:
      return 'pending_review';
    case GuideStatus.REJECTED:
      return 'rejected';
    case GuideStatus.SUSPENDED:
      return 'suspended';
    default:
      return 'draft';
  }
}

export const STATUS_GROUP_BADGE: Record<AdminGuideStatusGroup, StatusBadgeVariant> = {
  approved: 'approved',
  pending_review: 'pending',
  draft: 'profile_incomplete',
  rejected: 'rejected',
  suspended: 'suspended',
};

/** The precise underlying status, shown as a tooltip when the group hides detail. */
export const STATUS_DETAIL: Partial<Record<string, string>> = {
  [GuideStatus.SUBMITTED]: 'Submitted — not yet opened by an admin',
  [GuideStatus.UNDER_REVIEW]: 'Under review by an admin',
  [GuideStatus.PROFILE_INCOMPLETE]: 'Changes requested from the guide',
  [GuideStatus.DRAFT]: 'Guide has not submitted their profile yet',
};

export type GuideSortKey = 'recent' | 'newest' | 'oldest';

export const SORT_OPTIONS: Record<
  GuideSortKey,
  { label: string; sortBy: 'updatedAt' | 'createdAt'; sortOrder: 'asc' | 'desc' }
> = {
  recent: { label: 'Recently updated', sortBy: 'updatedAt', sortOrder: 'desc' },
  newest: { label: 'Newest', sortBy: 'createdAt', sortOrder: 'desc' },
  oldest: { label: 'Oldest', sortBy: 'createdAt', sortOrder: 'asc' },
};

export const DEFAULT_SORT: GuideSortKey = 'recent';

export function isSortKey(value: string | null): value is GuideSortKey {
  return value !== null && value in SORT_OPTIONS;
}

export function guideDisplayName(guide: AdminGuideListItem): string {
  const accountName = [guide.user?.firstName, guide.user?.lastName].filter(Boolean).join(' ');
  return guide.fullName || accountName || 'Unnamed guide';
}

export function guideInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0][0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

export function formatLocation(guide: AdminGuideListItem): string | null {
  return [guide.city, guide.state].filter(Boolean).join(', ') || null;
}

export function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Whether the guide's public /g/ card is currently reachable. */
export function hasPublicCard(guide: AdminGuideListItem): boolean {
  const state = guide.identity?.cardState;
  return Boolean(guide.identity) && (state === 'published' || state === 'changes_pending' || state === 'legacy');
}
